import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { UserPlus, UserCheck, Sparkles } from "lucide-react";
import { api, picUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function formatCount(n) {
  if (n == null) return "0";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return String(n);
}

const REASON_LABEL = {
  followed_by_friends: "followed by people you follow",
  matches_your_interests: "matches your interests",
  popular: "popular right now",
};

/**
 * Horizontal carousel of personalized creator suggestions.
 *
 * Props:
 *   limit: max creators to fetch (default 12)
 *   variant: "dark" (default) — used on black feed background
 *   title: heading text (default "// suggested.for.you")
 */
export default function SuggestedCreators({
  limit = 12,
  title = "// suggested.for.you",
  onDismissCreator,
}) {
  const { user } = useAuth();
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get("/discover/suggested-creators", { params: { limit } })
      .then((r) => {
        if (!cancelled) setCreators(r.data?.creators || []);
      })
      .catch(() => {
        if (!cancelled) setCreators([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [limit]);

  const toggleFollow = async (c) => {
    if (!user) {
      toast.error("Sign in to follow creators");
      return;
    }
    if (busy[c.user_id]) return;
    setBusy((b) => ({ ...b, [c.user_id]: true }));
    const wasFollowing = c.is_following;
    setCreators((prev) =>
      prev.map((x) =>
        x.user_id === c.user_id
          ? {
              ...x,
              is_following: !wasFollowing,
              follower_count: Math.max(
                0,
                (x.follower_count || 0) + (wasFollowing ? -1 : 1)
              ),
            }
          : x
      )
    );
    try {
      if (wasFollowing) {
        await api.delete(`/users/${c.username}/follow`);
      } else {
        await api.post(`/users/${c.username}/follow`);
        if (onDismissCreator) onDismissCreator(c.user_id);
      }
    } catch (e) {
      setCreators((prev) =>
        prev.map((x) =>
          x.user_id === c.user_id
            ? {
                ...x,
                is_following: wasFollowing,
                follower_count: Math.max(
                  0,
                  (x.follower_count || 0) + (wasFollowing ? 1 : -1)
                ),
              }
            : x
        )
      );
      toast.error("Could not update follow");
    } finally {
      setBusy((b) => ({ ...b, [c.user_id]: false }));
    }
  };

  if (!loading && creators.length === 0) return null;

  return (
    <div className="w-full" data-testid="suggested-creators">
      <div className="flex items-center gap-2 mb-3 px-1">
        <Sparkles className="h-3.5 w-3.5 text-[#39FF14]" strokeWidth={2} />
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-white/60">
          {title}
        </p>
      </div>

      <div
        className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide -mx-1 px-1 snap-x"
        data-testid="suggested-creators-scroll"
      >
        {loading && creators.length === 0
          ? Array.from({ length: 4 }).map((_, i) => (
              <div
                key={`sk-${i}`}
                className="shrink-0 w-44 h-56 border border-white/10 bg-white/5 animate-pulse"
              />
            ))
          : creators.map((c) => (
              <div
                key={c.user_id}
                data-testid={`suggested-${c.username}`}
                className="shrink-0 w-44 border border-white/10 bg-white/[0.03] hover:border-[#00FFFF]/50 transition-all snap-start p-4 flex flex-col items-center text-center"
              >
                <Link
                  to={`/u/${c.username}`}
                  className="flex flex-col items-center w-full"
                >
                  {c.picture ? (
                    <img
                      src={picUrl(c.picture)}
                      alt=""
                      className="h-16 w-16 object-cover border border-white/10 mb-3"
                    />
                  ) : (
                    <div className="h-16 w-16 bg-[#FF007F]/20 flex items-center justify-center font-bold text-[#FF007F] border border-white/10 mb-3 text-xl">
                      {c.name?.[0]?.toUpperCase()}
                    </div>
                  )}
                  <p className="font-semibold text-sm truncate w-full">
                    {c.name}
                  </p>
                  <p className="font-mono text-[10px] text-white/40 truncate w-full mb-1">
                    @{c.username}
                  </p>
                  <p className="font-mono text-[10px] text-[#00FFFF] mb-2">
                    {formatCount(c.follower_count)}{" "}
                    {c.follower_count === 1 ? "follower" : "followers"}
                  </p>
                  <p className="font-mono text-[9px] text-white/40 leading-tight h-8 line-clamp-2">
                    {REASON_LABEL[c.reason] || "suggested for you"}
                  </p>
                </Link>
                <button
                  data-testid={`suggested-follow-${c.username}`}
                  onClick={() => toggleFollow(c)}
                  disabled={!!busy[c.user_id]}
                  className={`w-full mt-3 flex items-center justify-center gap-1.5 px-3 py-2 font-mono text-[10px] uppercase tracking-widest transition-all disabled:opacity-40 ${
                    c.is_following
                      ? "border border-white/20 text-white/60 hover:text-white hover:border-white/40"
                      : "border border-[#39FF14]/60 text-[#39FF14] hover:bg-[#39FF14]/10"
                  }`}
                >
                  {c.is_following ? (
                    <>
                      <UserCheck className="h-3 w-3" /> Following
                    </>
                  ) : (
                    <>
                      <UserPlus className="h-3 w-3" /> Follow
                    </>
                  )}
                </button>
              </div>
            ))}
      </div>
    </div>
  );
}
